
import React, { useState } from 'react';
import {
  Box, Button, Typography, Tooltip, IconButton, useTheme,
  useMediaQuery, // Import useMediaQuery
  Menu,         // Import Menu
  MenuItem,     // Import MenuItem
  ListItemIcon  // Import ListItemIcon
} from '@mui/material';
import { useThemeContext } from '../context/ThemeContext';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SystemSelectorModal from './SystemSelectorModal';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSession } from '../context/SessionContext';

const NavbarActions: React.FC = () => {
  const theme = useTheme()
  const { darkMode, toggleDarkMode } = useThemeContext();
  const { selectedSystem, handleNavigation } = useSession();
  const [isModalOpen, setModalOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const isActionsMenuOpen = Boolean(actionsAnchorEl);

  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsAnchorEl(null);
  };

  return (
    <Box display="flex" alignItems="center">
      <Button
        variant="outlined"
        size="small"
        onClick={() => setModalOpen(true)}
        sx={{
          maxWidth: isMobile ? '150px' : '500px', // Constrain width on mobile
          minWidth: 'auto', // Allow it to shrink
          padding: isMobile ? '3px 8px' : '3px 12px',
          textTransform: 'none',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderColor: theme.palette.primary.light,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingRight: 1.5,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
        endIcon={<ArrowDropDownIcon />}
      >
        <Typography
          variant="subtitle1" // Adjust as needed (e.g. 'body1', 'h6', etc.)
          noWrap
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selectedSystem?.commonName || "Select a System"}
        </Typography>
      </Button>

      <SystemSelectorModal
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
      />

      {isMobile ? (
        // --- MOBILE VIEW ---
        <>
          <IconButton onClick={handleActionsMenuOpen}>
            <MoreVertIcon sx={{ color: theme.palette.text.primary }} />
          </IconButton>
          <Menu
            anchorEl={actionsAnchorEl}
            open={isActionsMenuOpen}
            onClose={handleActionsMenuClose}
          >
            <MenuItem onClick={() => { toggleDarkMode(); handleActionsMenuClose(); }}>
              <ListItemIcon>
                {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </ListItemIcon>
              Toggle Theme
            </MenuItem>
            <MenuItem onClick={() => { handleNavigation('/account'); handleActionsMenuClose(); }}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              Account
            </MenuItem>
            <MenuItem onClick={() => { handleNavigation('/logout'); handleActionsMenuClose(); }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </>
      ) : (
        // --- DESKTOP VIEW ---
        <>
          <Tooltip title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
            <IconButton
              onClick={toggleDarkMode}
              sx={{
                color: theme.palette.text.primary,
                minWidth: '48px',
                minHeight: '48px',
                marginLeft: 2,
              }}
            >
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Manage Accounts">
            <IconButton
              onClick={() => handleNavigation('/account')}
              sx={{
                color: theme.palette.text.primary,
                marginLeft: 1,
                minWidth: '48px',
                minHeight: '48px',
              }}
            >
              <AccountCircleIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Logout">
            <IconButton
              onClick={() => handleNavigation('/logout')}
              sx={{
                color: theme.palette.text.primary,
                marginLeft: 1,
                minWidth: '48px',
                minHeight: '48px',
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};
export default NavbarActions;